import { useEffect, useRef, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { Upload, FileText, X } from 'lucide-react'
import { Modal } from './ui/Modal'
import { ConfirmDialog } from './ui/ConfirmDialog'
import { Button } from './ui/Button'
import { api, extractApiError, isPlanLimitError } from '../lib/api'
import { generateSopResponse, sopSummary, sopListResponse } from '../lib/types'
import { formatNumber } from '../lib/format'

const ACCEPT =
  '.txt,.pdf,.docx,text/plain,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document'
const MAX_BYTES = 10 * 1024 * 1024

function fileKindLabel(file) {
  const ext = file.name.toLowerCase().split('.').pop() ?? ''
  if (ext === 'pdf') return 'PDF'
  if (ext === 'docx') return 'Word document'
  if (ext === 'txt') return 'Text file'
  return ext.toUpperCase() || 'File'
}

// Normalise a name for duplicate comparison: drop a trailing file
// extension, collapse whitespace, lowercase.
function compareName(name) {
  return String(name || '')
    .replace(/\.[a-z0-9]+$/i, '')
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase()
}

export function SopUploadDialog({ open, listId, onClose, onGenerated }) {
  const fileInputRef = useRef(null)
  const [file, setFile] = useState(null)
  const [dragOver, setDragOver] = useState(false)
  const [dupName, setDupName] = useState(null)
  const qc = useQueryClient()

  // Existing SOPs in this list — used to warn before uploading a duplicate
  // file. Same query key as SopListDialog, so the cache is shared.
  const existingSopsQuery = useQuery({
    queryKey: ['sops', listId],
    queryFn: async () => {
      const res = await api.get(`/lists/${listId}/sops`)
      return sopListResponse.parse(res.data).data
    },
    enabled: open && Boolean(listId),
  })

  useEffect(() => {
    if (open) {
      setFile(null)
      setDragOver(false)
      setDupName(null)
    }
  }, [open])

  function pickFile(picked) {
    if (!picked) return
    if (!/\.(txt|pdf|docx)$/i.test(picked.name)) {
      toast.error('Only .txt, .pdf, and .docx files are supported.')
      return
    }
    if (picked.size > MAX_BYTES) {
      toast.error('File must be 10 MB or smaller.')
      return
    }
    setFile(picked)
  }

  const uploadMutation = useMutation({
    mutationFn: async () => {
      if (!listId) throw new Error('No list context.')
      if (!file) throw new Error('Pick a file to upload.')
      const fd = new FormData()
      fd.append('file', file)
      const res = await api.post(`/lists/${listId}/sops/upload`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
        // The server parses the file AND calls Anthropic to draft
        // tasks in one round trip — the AI call alone can take 15–45s.
        // The global axios timeout (15s in lib/api.js) would abort the
        // request before Anthropic responds and the proxy in front of
        // the API would surface that as a 502 to the user. Override to
        // 120s so the frontend actually waits for the full response.
        timeout: 120_000,
      })
      const sop = sopSummary.parse(res.data.sop)
      // Preview is optional now — the upload route returns 201 with
      // `preview: null` + a structured `previewError` when the AI
      // pipeline failed (plan-limit hit, AI timeout, provider error,
      // …). Don't run the strict zod parse on null; pass null + the
      // server's error message through so the parent dialog can show
      // a meaningful retry instead of the generic
      // "Something went wrong" fallback the previous parse-throw
      // produced.
      const preview = res.data.preview
        ? generateSopResponse.parse(res.data.preview)
        : null
      const previewError = res.data.previewError ?? null
      return { sop, preview, previewError }
    },
    onSuccess: ({ sop, preview, previewError }) => {
      // Refresh the SOP list so the next duplicate check (and SopListDialog)
      // sees the new upload.
      void qc.invalidateQueries({ queryKey: ['sops', listId] })
      // SOP itself is saved — surface the AI failure (if any) as a
      // toast so the user knows why no preview opened, but still
      // hand the saved SOP up so the parent can show it in the
      // library list.
      if (!preview && previewError) {
        toast.error(previewError.message)
      }
      onGenerated(sop, preview, previewError)
    },
    onError: (err) => {
      // Plan-cap hit: the global API interceptor already shows the single
      // amber "upgrade your plan" warning, so suppress our own error toast.
      if (isPlanLimitError(err)) return
      toast.error(extractApiError(err).message)
    },
  })

  const existingSops = existingSopsQuery.data?.data ?? []

  // Warn (don't block) when the chosen file matches an existing SOP by name —
  // the user picked this exact file before. They can still upload anyway.
  function attemptUpload() {
    if (!file || !listId) return
    const target = compareName(file.name)
    const dupe = existingSops.find(
      (s) =>
        (s.originalFilename && compareName(s.originalFilename) === target) ||
        (s.title && compareName(s.title) === target),
    )
    if (dupe) {
      setDupName(dupe.title || dupe.originalFilename || file.name)
      return
    }
    uploadMutation.mutate()
  }

  return (
    <>
    <Modal
      open={open}
      onClose={onClose}
      title="Upload SOP"
      description="Upload a .txt, .pdf, or .docx file. TaskMagix extracts the text and drafts Tasks and Subtasks for you to review before they land in TO DO."
      size="md"
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={uploadMutation.isPending}>
            Cancel
          </Button>
          <Button
            onClick={attemptUpload}
            loading={uploadMutation.isPending}
            // Disabled while the existing-SOPs list is still loading, so the
            // duplicate-name check can't be skipped by an early click.
            disabled={!file || !listId || existingSopsQuery.isLoading}
          >
            Upload &amp; Generate
          </Button>
        </>
      }
    >
      <input
        ref={fileInputRef}
        type="file"
        accept={ACCEPT}
        onChange={(e) => {
          pickFile(e.target.files?.[0] ?? null)
          e.target.value = ''
        }}
        className="hidden"
      />

      {file ? (
        <div className="flex items-center gap-3 rounded-md border border-[color:var(--color-rule)] bg-[color:var(--color-card)] p-3">
          <span className="inline-flex items-center justify-center h-10 w-10 rounded bg-[color:var(--color-accent-bg)] text-[color:var(--color-accent)]">
            <FileText size={18} />
          </span>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-[color:var(--color-ink-strong)] truncate">
              {file.name}
            </p>
            <p className="text-xs text-[color:var(--color-ink-muted)]">
              {fileKindLabel(file)} · {formatNumber(file.size / 1024, { minimumFractionDigits: 1, maximumFractionDigits: 1, useGrouping: false })} KB
            </p>
          </div>
          <button
            type="button"
            onClick={() => setFile(null)}
            disabled={uploadMutation.isPending}
            title="Remove"
            aria-label="Remove file"
            className="p-1 rounded text-[color:var(--color-ink-muted)] hover:text-[color:var(--color-priority-urgent)] hover:bg-[color:var(--color-card-hover)] cursor-pointer disabled:opacity-50"
          >
            <X size={16} />
          </button>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => fileInputRef.current?.click()}
          onDragOver={(e) => {
            e.preventDefault()
            setDragOver(true)
          }}
          onDragLeave={() => setDragOver(false)}
          onDrop={(e) => {
            e.preventDefault()
            setDragOver(false)
            pickFile(e.dataTransfer.files?.[0] ?? null)
          }}
          className={
            'w-full rounded-lg border-2 border-dashed p-10 flex flex-col items-center justify-center gap-3 transition-colors cursor-pointer ' +
            (dragOver
              ? 'border-[color:var(--color-accent)] bg-[color:var(--color-accent-bg)]'
              : 'border-[color:var(--color-rule)] hover:border-[color:var(--color-accent)] hover:bg-[color:var(--color-card-hover)]')
          }
        >
          <span className="inline-flex items-center justify-center h-12 w-12 rounded-full bg-[color:var(--color-accent-bg)] text-[color:var(--color-accent)]">
            <Upload size={20} />
          </span>
          <div className="text-center">
            <p className="text-sm font-medium text-[color:var(--color-ink-strong)]">
              Click to choose a file or drop it here
            </p>
            <p className="mt-1 text-xs text-[color:var(--color-ink-muted)]">
              Accepts .txt, .pdf, .docx · up to 10 MB
            </p>
          </div>
        </button>
      )}
    </Modal>

      <ConfirmDialog
        open={dupName !== null}
        title="Duplicate SOP Name"
        message={
          dupName
            ? `A SOP named "${dupName}" already exists in this list. Upload another copy anyway?`
            : ''
        }
        confirmLabel="Upload Anyway"
        variant="primary"
        confirming={uploadMutation.isPending}
        onCancel={() => setDupName(null)}
        onConfirm={() => {
          setDupName(null)
          uploadMutation.mutate()
        }}
      />
    </>
  )
}
